import queryString from 'query-string'
import { isNil } from 'lodash'

import { apiService, fetchingAPI } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'
import { fetchOrganizations } from '@/reducers/organizations/organizations.actions'

import {
  setLoading,
  setError,
  setTenants,
  setAgents,
  setCartesiaAgents,
  upsertAgent,
} from './agentProvisioning.redux'

// =============================================================================
// Async thunks for the agent-creation wizard (AVR-435).
//
// These hit the voice-agents service *root* (not /prompts): the new
// provisioning endpoints plus the existing /voice-agent-configs CRUD.
//
// Org-scoping mirrors voiceAgents.actions.js exactly: fetchingAPI injects the
// caller's `organizationid` header; a Balto admin (org 1) targets another org
// via the `requested_organization_id` query param — never a header override.
// Passing a falsy orgId omits the param so the BE defaults to the caller's org.
//
// Convention: read thunks dispatch into the slice and return the data; write
// thunks return a result object `{ ok, data?, kind?, message? }` so the wizard
// can advance steps and render inline field errors. handleResponse rejects
// 409/400 with the raw JSON body, so a structured `kind` discriminator is read
// from `error.kind` (see ERROR_KIND in the constants).
//
// Request bodies are snake_case (matches the existing VoiceAgentConfigCreate
// schema / FastAPI defaults). New endpoints are expected to return camelCase
// to match the FE's existing voice-agents consumption — notably
// `GET /voice-agents` returns each row's `configs` array.
// =============================================================================

const root = () => apiService.voiceAgents

// `requested_organization_id` is honored ONLY by the agent-scoped endpoints
// (POST /voice-agents/provision, GET /voice-agents). The tenant / validate /
// cartesia-list endpoints ignore it (admin access there is gated by the
// session), so those calls omit it.
const orgQuery = (orgId) => queryString.stringify({ requested_organization_id: orgId || undefined })

const withOrg = (url, orgId) => {
  const params = orgQuery(orgId)
  return params ? `${url}?${params}` : url
}

// FastAPI's 422 body is `detail: [{ msg, loc }, ...]` rather than a string, so
// a plain `error.detail` renders as "[object Object]". Pull the first field's
// message out and drop pydantic's "Value error, " prefix, which is noise to the
// person reading it.
const detailMessage = (detail) => {
  if (typeof detail === 'string') {
    return detail
  }
  const first = Array.isArray(detail) ? detail.find((entry) => entry?.msg) : null
  return first ? String(first.msg).replace(/^Value error, /, '') : null
}

const normalizeError = (error, fallback) => ({
  ok: false,
  kind: error?.kind ?? null,
  status: error?.status ?? null,
  // voice-agents uses `detail`; the core monolith uses `error_message`; plain
  // Errors carry `message`. Prefer the BE's specific reason over the fallback.
  message:
    detailMessage(error?.detail) ||
    error?.error_message ||
    error?.message ||
    (typeof error === 'string' ? error : fallback),
})

// -----------------------------------------------------------------------------
// Step 1 — identity (org + the agent's own ai_agent user). These hit the core
// monolith (apiService.core), not voice-agents: an agent's org is derived from
// its user's organization_id, and the agent *is* a user row (users.ai_agent).
// The existing organizations/createOrganization + organizationUsers/createUser
// thunks aren't reusable here (one navigates away, the other returns nothing),
// so these dedicated thunks return the created record.
// -----------------------------------------------------------------------------

export const createAgentOrganization =
  ({ name }) =>
  async (dispatch) => {
    dispatch(setLoading({ createOrganization: true }))
    dispatch(setError({ createOrganization: null }))
    try {
      const data = await fetchingAPI(
        `${apiService.core}/api/organizations`,
        'POST',
        dispatch,
        JSON.stringify({ name })
      )
      // Refresh the app-wide org list so the new org shows in the dropdown.
      await dispatch(fetchOrganizations())
      toast.success('Organization created')
      return { ok: true, data }
    } catch (error) {
      // Inline error in the create-org sub-form (no toast — it's field-level).
      const result = normalizeError(error, "Couldn't create the organization. Please try again.")
      dispatch(setError({ createOrganization: result.message }))
      return result
    } finally {
      dispatch(setLoading({ createOrganization: false }))
    }
  }

// Creates the agent's user row with ai_agent = true (Balto-admin only flag).
// orgId is the path-scoped org the agent belongs to. `configCid` is the agent's
// Playbook — it lands on `users.config_cid`, which voice-agents reads back for
// org resolution + post-call ingestion.
//
// Always creates a NEW user — it does not reuse or look up pre-existing users.
// (Relying on "orphan" users left by partially-completed runs is unreliable,
// especially since users are created in other places too. If create fails — e.g.
// the username is taken — we surface the error rather than reusing.) The core
// create endpoint returns `{ "OK": "OK" }`, not the new user, so we recover its
// id afterward from the voice-agents `/org-users` list by the (unique) username.
export const createAgentUser =
  ({ username, firstName, lastName, configCid }, orgId) =>
  async (dispatch) => {
    dispatch(setLoading({ createAgentUser: true }))
    dispatch(setError({ createAgentUser: null }))
    try {
      await fetchingAPI(
        `${apiService.core}/api/organizations/${orgId}/users`,
        'POST',
        dispatch,
        JSON.stringify({
          username,
          first_name: firstName,
          last_name: lastName,
          config_cid: configCid,
          ai_agent: true,
          deleted: false,
        })
      )
      const orgUsers = await fetchingAPI(withOrg(`${root()}/org-users`, orgId), 'GET', dispatch)
      const created = (orgUsers?.items || []).find((user) => user.username === username)
      if (!created) {
        const message = 'Agent user was created but could not be looked up — try again.'
        dispatch(setError({ createAgentUser: message }))
        toast.error(message)
        return { ok: false, message }
      }
      toast.success('Agent user created')
      return { ok: true, data: created }
    } catch (error) {
      const result = normalizeError(error, "Couldn't create the agent user. Please try again.")
      dispatch(setError({ createAgentUser: result.message }))
      toast.error(result.message, { cause: error })
      return result
    } finally {
      dispatch(setLoading({ createAgentUser: false }))
    }
  }

// -----------------------------------------------------------------------------
// Step 2 — Cartesia tenants
// -----------------------------------------------------------------------------

// Tenants are global (not org-scoped) — no requested_organization_id.
export const fetchTenants = () => async (dispatch) => {
  dispatch(setLoading({ tenants: true }))
  dispatch(setError({ tenants: null }))
  try {
    const data = await fetchingAPI(`${root()}/cartesia-tenants`, 'GET', dispatch)
    const items = Array.isArray(data?.items) ? data.items : []
    dispatch(setTenants({ items }))
    return items
  } catch (error) {
    dispatch(setError({ tenants: error.message || 'Failed to load tenants' }))
    toast.error('Failed to load Cartesia tenants', { cause: error })
    return []
  } finally {
    dispatch(setLoading({ tenants: false }))
  }
}

// Org-1 only — gated by the session, not the param (no requested_organization_id).
export const createTenant =
  ({ name, tokenEnvVar }) =>
  async (dispatch) => {
    dispatch(setLoading({ createTenant: true }))
    dispatch(setError({ createTenant: null }))
    try {
      const data = await fetchingAPI(
        `${root()}/cartesia-tenants`,
        'POST',
        dispatch,
        JSON.stringify({ name, token_env_var: tokenEnvVar })
      )
      toast.success('Tenant created')
      return { ok: true, data }
    } catch (error) {
      // Inline error in the create-tenant sub-form (no toast — it's field-level).
      const result = normalizeError(error, "Couldn't create the Cartesia tenant. Please try again.")
      dispatch(setError({ createTenant: result.message }))
      return result
    } finally {
      dispatch(setLoading({ createTenant: false }))
    }
  }

// -----------------------------------------------------------------------------
// Step 3 — voice_agents master row
// -----------------------------------------------------------------------------

// Final step — atomically create the voice_agents row AND its env config in one
// transaction (POST /voice-agents/provision). Either both land or neither does,
// so an abandoned flow before this call leaves no orphan. The slug is
// operator-supplied (the one the agent's code calls, usually its ROUTER_NAME —
// the BE can't derive it) — or null for an agent without backend code, in which
// case the key is omitted and the BE derives the slug from the prompt-config
// seed's agent id and echoes it in the response (AVR-836/837). Re-validates
// everything server-side first; error `kind`s are slug_not_deployed (422 — no
// code router released here; `allowUnreleasedSlug` overrides it once the
// operator confirms), slug_reserved_shape (422), slug_taken / agent_id_in_use /
// prompt_config_exists (409), and owner_* (422, defensive). Returns the new
// agent + its config.
export const provisionVoiceAgent =
  (
    {
      slug,
      useCaseName,
      cartesiaTenantId,
      userId,
      agentId,
      seedPromptConfig,
      promptConfig,
      promptConfigAgentId,
      allowUnreleasedSlug,
    },
    orgId
  ) =>
  async (dispatch) => {
    dispatch(setLoading({ provision: true }))
    dispatch(setError({ provision: null }))
    try {
      const body = {
        use_case_name: useCaseName,
        cartesia_tenant_id: cartesiaTenantId,
        user_id: userId,
        agent_id: agentId,
      }
      // Omitted, not null: "no slug" is what selects the derived-slug path.
      if (!isNil(slug)) {
        body.slug = slug
      }
      // Sent only after the operator confirms an unreleased slug.
      if (allowUnreleasedSlug) {
        body.allow_unreleased_slug = true
      }
      // Prompt-config seeding (AVR-559, Balto admins only): rides the same
      // atomic provision transaction. A pasted config is sent verbatim (it
      // carries its own PE agent_id); otherwise the PE id alone asks the BE to
      // build an empty shell. Fields are omitted entirely when seeding is off —
      // the BE 422s on prompt fields without the flag.
      if (seedPromptConfig) {
        body.seed_prompt_config = true
        if (promptConfig) {
          body.prompt_config = promptConfig
        } else {
          body.prompt_config_agent_id = promptConfigAgentId
        }
      }
      const data = await fetchingAPI(
        withOrg(`${root()}/voice-agents/provision`, orgId),
        'POST',
        dispatch,
        JSON.stringify(body)
      )
      toast.success('AI agent provisioned')
      return { ok: true, data }
    } catch (error) {
      // The BE detail is surfaced on the step (inline, or in the
      // slug_not_deployed confirm). No toast.
      const result = normalizeError(error, "Couldn't provision the AI agent. Please try again.")
      dispatch(setError({ provision: result.message }))
      return result
    } finally {
      dispatch(setLoading({ provision: false }))
    }
  }

// -----------------------------------------------------------------------------
// Agents list — voice_agents rows with their configs
// -----------------------------------------------------------------------------

// Feeds the agents list page. Each item (BE is snake_case): { id, slug,
// use_case_name, user_id, organization_id, cartesia_tenant_id, deployed,
// owner_username, configs: [{ id, agent_id }] }. An empty `configs` means a
// legacy pre-atomic partial provision.
export const fetchVoiceAgents = (orgId) => async (dispatch, getState) => {
  // Several components may ask for the list in the same render flush (e.g.
  // AgentHeader and OverviewTab on a deep link) — dedupe here, where the
  // loading flag is already visible synchronously.
  if (getState().agentProvisioning.loading.agents) {
    return []
  }
  dispatch(setLoading({ agents: true }))
  dispatch(setError({ agents: null }))
  try {
    const data = await fetchingAPI(withOrg(`${root()}/voice-agents`, orgId), 'GET', dispatch)
    const items = Array.isArray(data?.items) ? data.items : []
    dispatch(setAgents({ items, orgId: data?.orgId ?? orgId ?? null }))
    return items
  } catch (error) {
    dispatch(setError({ agents: error.message || 'Failed to load AI agents' }))
    toast.error('Failed to load AI agents', { cause: error })
    return []
  } finally {
    dispatch(setLoading({ agents: false }))
  }
}

// Rename an agent (AVR-827). An agent *is* its `ai_agent` user, so the name on
// the card, the header and Call Explorer is that user's first + last name. The
// whole name goes over as one string and the server splits it across the two
// columns, so the split convention lives in one place rather than in every
// caller — `voice_agent_id` and `slug` are the agent's identity and the
// endpoint cannot touch them.
//
// Deliberately NOT core's `PATCH /api/users/:id`, even though that is where
// the row lives: core gates it on the `edit_users` permission, which org
// admins hold and Studio editors generally do not. Routing through
// voice-agents puts the rename behind the Studio permission the page already
// requires, so everyone who can open an agent can rename it — customers
// included — and nobody else can.
//
// The response is the agent's full roster shape, so the slice swaps the whole
// row and no refetch is needed.
export const renameVoiceAgent =
  ({ voiceAgentId, name, orgId }) =>
  async (dispatch) => {
    dispatch(setLoading({ renameAgent: true }))
    dispatch(setError({ renameAgent: null }))
    try {
      const data = await fetchingAPI(
        withOrg(`${root()}/voice-agents/${voiceAgentId}`, orgId),
        'PATCH',
        dispatch,
        JSON.stringify({ name }),
        undefined,
        // The modal renders the failure inline; without this `handleResponse`
        // also fires a toast, so one vague error appears twice.
        { isSilent: true }
      )
      dispatch(upsertAgent(data))
      toast.success('Agent renamed')
      return { ok: true, data }
    } catch (error) {
      const result = normalizeError(error, 'Failed to rename agent')
      dispatch(setError({ renameAgent: result.message }))
      return result
    } finally {
      dispatch(setLoading({ renameAgent: false }))
    }
  }

// Change an agent's slug (AVR-886), Balto admin org only. The BE 403s unless the
// `organizationid` header is 1, so the header carries the user's own org rather
// than the org they are viewing: an admin switched into a customer org still
// gets through, and nobody else can. Same slug rules as provision:
// slug_taken (409), slug_reserved_shape and slug_not_deployed (422, the latter
// overridable with `allowUnreleasedSlug` after a confirm), and a plain-detail
// 422 for a malformed slug. Returns the roster shape, like rename, so the row
// is swapped in place.
export const changeVoiceAgentSlug =
  ({ voiceAgentId, slug, allowUnreleasedSlug }) =>
  async (dispatch) => {
    dispatch(setLoading({ changeSlug: true }))
    dispatch(setError({ changeSlug: null }))
    try {
      const body = { slug }
      if (allowUnreleasedSlug) {
        body.allow_unreleased_slug = true
      }
      const data = await fetchingAPI(
        `${root()}/voice-agents/${voiceAgentId}/slug`,
        'PATCH',
        dispatch,
        JSON.stringify(body),
        undefined,
        // The dialog renders the failure inline.
        { isSilent: true },
        true
      )
      dispatch(upsertAgent(data))
      toast.success('Slug changed')
      return { ok: true, data }
    } catch (error) {
      const result = normalizeError(error, 'Failed to change slug')
      dispatch(setError({ changeSlug: result.message }))
      return result
    } finally {
      dispatch(setLoading({ changeSlug: false }))
    }
  }

// Pick-by-name: list a tenant's Cartesia agents so the operator selects by name
// instead of pasting an opaque id. Admin-org only and needs a configured token,
// so 400/403/502 are expected — fall back to manual paste (return [], no toast).
export const fetchCartesiaAgents = (cartesiaTenantId) => async (dispatch) => {
  if (!cartesiaTenantId) {
    dispatch(setCartesiaAgents({ items: [], tenantId: null }))
    return []
  }
  dispatch(setLoading({ cartesiaAgents: true }))
  dispatch(setError({ cartesiaAgents: null }))
  try {
    // Org-1 only, gated by the session — not org-scoped via the param.
    const params = queryString.stringify({ cartesia_tenant_id: cartesiaTenantId })
    const data = await fetchingAPI(`${root()}/cartesia/agents?${params}`, 'GET', dispatch)
    const items = Array.isArray(data?.items) ? data.items : []
    dispatch(setCartesiaAgents({ items, tenantId: cartesiaTenantId }))
    return items
  } catch (error) {
    dispatch(setCartesiaAgents({ items: [], tenantId: cartesiaTenantId }))
    dispatch(setError({ cartesiaAgents: error.message || null }))
    return []
  } finally {
    dispatch(setLoading({ cartesiaAgents: false }))
  }
}

// -----------------------------------------------------------------------------
// Step 1 — validate the Cartesia agent_id (read-only, no write)
// -----------------------------------------------------------------------------

// POST /voice-agents/provision/validate: the BE confirms the agent exists in
// Cartesia and returns { ok, agent: { id, name }, router_name_set } — the name
// lets the operator confirm they linked the right agent; router_name_set
// (true | false | null, AVR-836) feeds step 4's hint. No slug in the response
// (it's operator-supplied at the last step) and no deploy check here — that
// happens at provision, keyed on the slug. Failures: 409 agent_id_in_use; 422
// agent_id_unknown (not in Cartesia), surfaced via the BE detail. Not
// org-scoped — keyed on the tenant.
export const validateCartesiaAgent =
  ({ agentId, cartesiaTenantId }) =>
  async (dispatch) => {
    dispatch(setLoading({ validateAgent: true }))
    dispatch(setError({ validateAgent: null }))
    try {
      const data = await fetchingAPI(
        `${root()}/voice-agents/provision/validate`,
        'POST',
        dispatch,
        JSON.stringify({ agent_id: agentId, cartesia_tenant_id: cartesiaTenantId })
      )
      return { ok: true, data }
    } catch (error) {
      const result = normalizeError(
        error,
        "Couldn't validate that agent id. Check the id exists in Cartesia."
      )
      dispatch(setError({ validateAgent: result.message }))
      return result
    } finally {
      dispatch(setLoading({ validateAgent: false }))
    }
  }
